// Component: ModifyOrderModal.jsx
// Purpose: Operator-facing Modify order modal (R6.1-R6.7). Fetches the per-broker
//          modify-capability descriptor + the order's current values, pre-fills a
//          capability-driven form (never a fixed union of both brokers' fields/options),
//          validates client-side, confirms before a market-executing conversion, and
//          submits CANONICAL values only via OrdersService.modifyOrder.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OrdersService from '../../api/services/orders';
import { Button } from '../ds';
import ConfirmDialog from './ConfirmDialog';

/*>>> CONSTANTS <<<*/

//! R6.6 — canonical order_type values that execute immediately at market. Selecting either
//  requires the FE confirm dialog (mirrors R5.2.1's cancel-confirm pattern) before submit.
const MARKET_EXECUTING_TYPES = new Set(['MARKET', 'SL_M']);

// --> Client-side cross-field heuristic (R6.3) — the OMS-side descriptor's full
//     order_type_requirements matrix (R2.1.3) is authoritative; this is a light client-side
//     pre-check so an operator doesn't round-trip to the server for an obviously-missing field.
const LIMIT_LIKE_TYPES = new Set(['LIMIT', 'SL']);       // require a limit_price
const TRIGGER_LIKE_TYPES = new Set(['SL', 'SL_M']);      // require a trigger_price

/*>>> HELPERS <<<*/

// --> R6.7 — dual-layer label: canonical display name + plain-language definition +
//     this order's broker's native raw term, e.g.
//     "Stop-Limit — triggers a limit order at your stop price (ICICI: stoploss)".
//     brokerLabel falls back to the generic word "broker" when no broker display name is
//     available on the order row (see the CONTRACT ASSUMPTION note in the module docstring
//     below the imports — the documented `/modify-capabilities` response shape has no
//     broker-name field, only per-option raw_term).
const formatCapabilityLabel = (item, brokerLabel) =>
  `${item.display_name} — ${item.definition} (${brokerLabel}: ${item.raw_term})`;

const _toNumOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const _asInputValue = (v) => (v == null ? '' : String(v));

/*>>> SUB-COMPONENTS <<<*/

// --> Labeled form field with inline validation error (R6.3)
const FormField = ({ label, error, children }) => (
  <div>
    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-fg-2">
      {label}
    </label>
    {children}
    {error && <p className="mt-1 text-[11px] text-loss">{error}</p>}
  </div>
);

const inputCls =
  'w-full rounded-md border border-divider bg-bg-3 px-2.5 py-1.5 text-sm text-fg-1 focus:border-[var(--primary)] focus:outline-none';
const selectCls = inputCls;

/*>>> MAIN COMPONENT <<<*/

/**
 * ModifyOrderModal — new modal, no existing analogue found (grepped `frontend/src/components`
 * for `Modal|Dialog|Drawer` — only `OrderDetailDrawer` exists, a read-only side drawer with a
 * different interaction shape; this mirrors its backdrop+Escape pattern via `ConfirmDialog`
 * for the nested confirm step, not a full re-derivation).
 *
 * @param {boolean}  open
 * @param {Object}   order      - the order row (order_id, symbol, exchange, status, ...) from OrdersTable
 * @param {Function} onClose    - () => void
 * @param {Function} onSuccess  - (orderId) => void, fired after a confirmed modify submit resolves
 */
const ModifyOrderModal = ({ open, order, onClose, onSuccess }) => {
  /*>>> STATE <<<*/

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);

  // Tier-1 fields (all brokers)
  const [qty, setQty] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [triggerPrice, setTriggerPrice] = useState('');
  // Tier-2 fields (per-broker capability-driven)
  const [orderType, setOrderType] = useState('');
  const [validity, setValidity] = useState('');
  const [disclosedQty, setDisclosedQty] = useState('');
  // OMS_INTERNAL fields (R9.2 — never-routed MONITORING order)
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');

  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // R6.6 — market-conversion confirm step
  const [marketConfirmOpen, setMarketConfirmOpen] = useState(false);
  const [pendingSubmitFields, setPendingSubmitFields] = useState(null);

  /*>>> EFFECTS <<<*/

  //! Fetch capabilities + current order values on open. Both calls are gateway-routed via
  //  OrdersService — never a direct microservice call.
  useEffect(() => {
    if (!open || !order?.order_id) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setCapabilities(null);
    setOrderDetail(null);

    (async () => {
      try {
        const [capRes, orderRes] = await Promise.all([
          OrdersService.getModifyCapabilities(order.order_id),
          OrdersService.getOrderById(order.order_id),
        ]);
        if (cancelled) return;

        const cap = capRes?.data?.data ?? capRes?.data ?? null;
        const detail = orderRes?.data?.data ?? orderRes?.data ?? null;
        setCapabilities(cap);
        setOrderDetail(detail);

        // --> R6.2 pre-fill: DIRECT passthrough — Order.order_type/validity are already
        //     canonical (02-design.md iter-3), so no reverse-lookup is needed.
        setQty(_asInputValue(detail?.qty));
        setLimitPrice(_asInputValue(detail?.limit_price));
        setTriggerPrice(_asInputValue(detail?.trigger_price));
        setOrderType(detail?.order_type ?? '');
        setValidity(detail?.validity ?? '');
        setDisclosedQty(''); // accepted gap — Order never persists disclosed_quantity (02-design.md Data lineage §2)
        setStopLoss(_asInputValue(detail?.stop_loss));
        setTakeProfit(_asInputValue(detail?.take_profit));
      } catch (err) {
        if (cancelled) return;
        console.log('[DIAG] ModifyOrderModal — failed to load capabilities/order detail', err);
        setLoadError(
          err?.response?.data?.error?.message || err?.message || 'Failed to load order for modify'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, order?.order_id]);

  // --> Reset transient (non-fetch) state whenever the modal closes
  useEffect(() => {
    if (!open) {
      setValidationErrors({});
      setSubmitError(null);
      setMarketConfirmOpen(false);
      setPendingSubmitFields(null);
    }
  }, [open]);

  /*>>> DERIVED <<<*/

  const isOmsInternal = capabilities?.mode === 'OMS_INTERNAL';
  const isBroker = capabilities?.mode === 'BROKER';

  //* CONTRACT ASSUMPTION — the documented `/modify-capabilities` response shape has no
  //  broker-display-name field (only per-option `raw_term`). Fall back through whatever the
  //  order row/detail may carry, else the generic word "broker" (matches the coordinator's
  //  own example format literally).
  const brokerLabel =
    order?.broker_name || order?.broker || orderDetail?.broker_name || orderDetail?.broker || 'broker';

  /*>>> VALIDATION (R6.3) <<<*/

  const validate = useCallback(() => {
    const errs = {};

    if (isOmsInternal) {
      if (stopLoss !== '' && isNaN(Number(stopLoss))) errs.stopLoss = 'Stop loss must be numeric';
      if (takeProfit !== '' && isNaN(Number(takeProfit))) errs.takeProfit = 'Take profit must be numeric';
      setValidationErrors(errs);
      return Object.keys(errs).length === 0;
    }

    const qtyNum = Number(qty);
    if (qty === '' || isNaN(qtyNum) || qtyNum <= 0 || !Number.isInteger(qtyNum)) {
      errs.qty = 'Quantity must be a positive whole number';
    }

    if (LIMIT_LIKE_TYPES.has(orderType)) {
      const p = Number(limitPrice);
      if (limitPrice === '' || isNaN(p) || p <= 0) {
        errs.limitPrice = 'Limit price is required for this order type';
      }
    }
    if (TRIGGER_LIKE_TYPES.has(orderType)) {
      const t = Number(triggerPrice);
      if (triggerPrice === '' || isNaN(t) || t <= 0) {
        errs.triggerPrice = 'Trigger price is required for this order type';
      }
    }
    if (capabilities?.disclosed_quantity_supported && disclosedQty !== '') {
      const dq = Number(disclosedQty);
      if (isNaN(dq) || dq < 0 || (!isNaN(qtyNum) && dq > qtyNum)) {
        errs.disclosedQty = 'Disclosed quantity must be between 0 and the order quantity';
      }
    }

    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }, [isOmsInternal, stopLoss, takeProfit, qty, orderType, limitPrice, triggerPrice, disclosedQty, capabilities]);

  /*>>> FIELD DIFF (only changed fields go on the wire) <<<*/

  const buildChangedFields = useCallback(() => {
    if (isOmsInternal) {
      const fields = {};
      if (stopLoss !== _asInputValue(orderDetail?.stop_loss)) fields.stop_loss = _toNumOrNull(stopLoss);
      if (takeProfit !== _asInputValue(orderDetail?.take_profit)) fields.take_profit = _toNumOrNull(takeProfit);
      return fields;
    }

    const fields = {};
    if (qty !== _asInputValue(orderDetail?.qty)) fields.qty = _toNumOrNull(qty);
    if (limitPrice !== _asInputValue(orderDetail?.limit_price)) fields.limit_price = _toNumOrNull(limitPrice);
    if (triggerPrice !== _asInputValue(orderDetail?.trigger_price)) fields.trigger_price = _toNumOrNull(triggerPrice);
    if (orderType && orderType !== orderDetail?.order_type) fields.order_type = orderType;
    if (validity && validity !== orderDetail?.validity) fields.validity = validity;
    if (capabilities?.disclosed_quantity_supported && disclosedQty !== '') {
      fields.disclosed_quantity = _toNumOrNull(disclosedQty);
    }
    return fields;
  }, [isOmsInternal, stopLoss, takeProfit, qty, limitPrice, triggerPrice, orderType, validity, disclosedQty, orderDetail, capabilities]);

  /*>>> SUBMIT (R6.4) <<<*/

  const doSubmit = useCallback(
    async (fields) => {
      if (!order?.order_id) return;
      setSubmitting(true);
      setSubmitError(null);
      try {
        console.log('[DIAG] ModifyOrderModal — submitting modify', { orderId: order.order_id, fields });
        await OrdersService.modifyOrder(order.order_id, fields);
        onSuccess?.(order.order_id);
      } catch (err) {
        console.log('[DIAG] ModifyOrderModal — modify submit failed', err);
        setSubmitError(err?.response?.data?.error?.message || err?.message || 'Modify failed');
      } finally {
        setSubmitting(false);
      }
    },
    [order, onSuccess]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (submitting) return;
      if (!validate()) return;

      const fields = buildChangedFields();
      if (Object.keys(fields).length === 0) {
        setSubmitError('No changes to submit');
        return;
      }

      //! R6.6 — a market-executing order_type target requires an explicit confirm before
      //  the request is sent (fills immediately at the best available price).
      if (isBroker && MARKET_EXECUTING_TYPES.has(fields.order_type)) {
        setPendingSubmitFields(fields);
        setMarketConfirmOpen(true);
        return;
      }

      doSubmit(fields);
    },
    [submitting, validate, buildChangedFields, isBroker, doSubmit]
  );

  const handleMarketConfirm = useCallback(() => {
    setMarketConfirmOpen(false);
    const fields = pendingSubmitFields;
    setPendingSubmitFields(null);
    if (fields) doSubmit(fields);
  }, [pendingSubmitFields, doSubmit]);

  const handleMarketConfirmCancel = useCallback(() => {
    setMarketConfirmOpen(false);
    setPendingSubmitFields(null);
  }, []);

  // --> R6.6 message (AC re-issued 2026-07-05): the confirm-GATE is FIRM (a market-executing
  //     conversion cannot submit without an explicit confirm — see handleSubmit/marketConfirm).
  //     The LTP+slippage-delta CONTENT is best-effort: no `ltp`/`last_price` field exists on the
  //     order response today, so this ALWAYS degrades to the GENERIC immediate-fill warning below
  //     — WITHOUT a fabricated price. Surfacing live LTP is a deferred enhancement (a follow-up to
  //     add an `ltp` field to the order response); the LTP branches here light up automatically if
  //     that field ever arrives. Degrading to the generic warning is the specified v1 behavior.
  const marketConvertMessage = useMemo(() => {
    const ltpRaw = order?.ltp ?? order?.last_price ?? orderDetail?.ltp ?? null;
    const oldLimit = orderDetail?.limit_price != null ? Number(orderDetail.limit_price) : null;
    const base =
      'Converting to a market-executing order type fills immediately at the best available price, which may differ from your prior limit price.';
    const ltpNum = ltpRaw != null ? Number(ltpRaw) : null;
    if (ltpNum == null || isNaN(ltpNum)) {
      return `${base} Current LTP is unavailable in this view — confirm you accept the immediate-fill / slippage risk before proceeding.`;
    }
    if (oldLimit != null && !isNaN(oldLimit)) {
      const delta = ltpNum - oldLimit;
      return `${base} Current LTP ₹${ltpNum.toFixed(2)} vs your limit ₹${oldLimit.toFixed(2)} (${delta >= 0 ? '+' : ''}${delta.toFixed(2)}).`;
    }
    return `${base} Current LTP ₹${ltpNum.toFixed(2)}.`;
  }, [order, orderDetail]);

  /*>>> RENDER <<<*/
  if (!open || !order) return null;

  // instrument-id-display-remediation — display_symbol-first (stored canonical name); never a uuid.
  const symbol = order.display_symbol ?? order.symbol ?? order.name ?? 'Order';
  const orderId = order.order_id ?? order.orderId;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={() => !submitting && onClose?.()} />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Modify order"
        className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-divider/50 bg-bg-2 shadow-2xl"
      >
        {/*>>> HEADER <<<*/}
        <div className="border-b border-divider/50 bg-bg-3/60 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="ds-h2 truncate tracking-tight text-fg-1">Modify {symbol}</h2>
              <p className="mt-0.5 font-mono text-xs text-fg-2">{orderId}</p>
            </div>
            <button
              onClick={() => onClose?.()}
              className="shrink-0 rounded-md p-1.5 text-fg-2 transition-colors hover:bg-bg-3 hover:text-fg-1"
              aria-label="Close modify order modal"
            >
              ✕
            </button>
          </div>
        </div>

        {/*>>> BODY <<<*/}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <p className="text-sm text-fg-2">Loading current order values…</p>}

          {!loading && loadError && (
            <p className="text-sm text-loss">{loadError}</p>
          )}

          {!loading && !loadError && capabilities && (
            <form id="modify-order-form" onSubmit={handleSubmit} className="space-y-4">
              {isOmsInternal && (
                <>
                  <p className="text-xs text-fg-2">
                    This order has never been routed to a broker (monitoring-only). Only stop-loss / take-profit are editable.
                  </p>
                  <FormField label="Stop Loss" error={validationErrors.stopLoss}>
                    <input
                      type="number"
                      step="0.05"
                      className={inputCls}
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                    />
                  </FormField>
                  <FormField label="Take Profit" error={validationErrors.takeProfit}>
                    <input
                      type="number"
                      step="0.05"
                      className={inputCls}
                      value={takeProfit}
                      onChange={(e) => setTakeProfit(e.target.value)}
                    />
                  </FormField>
                </>
              )}

              {isBroker && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Quantity" error={validationErrors.qty}>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={inputCls}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      />
                    </FormField>

                    {/* R6.5/R6.7 — options restricted to THIS broker's capability descriptor */}
                    <FormField label="Order Type">
                      <select
                        className={selectCls}
                        value={orderType}
                        onChange={(e) => setOrderType(e.target.value)}
                      >
                        {(capabilities.order_types || []).map((ot) => (
                          <option key={ot.canonical} value={ot.canonical}>
                            {formatCapabilityLabel(ot, brokerLabel)}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Limit Price" error={validationErrors.limitPrice}>
                      <input
                        type="number"
                        step="0.05"
                        className={inputCls}
                        value={limitPrice}
                        onChange={(e) => setLimitPrice(e.target.value)}
                      />
                    </FormField>
                    <FormField label="Trigger Price" error={validationErrors.triggerPrice}>
                      <input
                        type="number"
                        step="0.05"
                        className={inputCls}
                        value={triggerPrice}
                        onChange={(e) => setTriggerPrice(e.target.value)}
                      />
                    </FormField>
                  </div>

                  <FormField label="Validity">
                    <select
                      className={selectCls}
                      value={validity}
                      onChange={(e) => setValidity(e.target.value)}
                    >
                      {(capabilities.validities || []).map((v) => (
                        <option key={v.canonical} value={v.canonical}>
                          {formatCapabilityLabel(v, brokerLabel)}
                        </option>
                      ))}
                    </select>
                  </FormField>

                  {/* R6.5 — disclosed_quantity rendered ONLY when this broker supports it (Breeze) */}
                  {capabilities.disclosed_quantity_supported && (
                    <FormField label="Disclosed Quantity" error={validationErrors.disclosedQty}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={inputCls}
                        placeholder="—"
                        value={disclosedQty}
                        onChange={(e) => setDisclosedQty(e.target.value)}
                      />
                    </FormField>
                  )}
                </>
              )}

              {submitError && <p className="text-sm text-loss">{submitError}</p>}
            </form>
          )}
        </div>

        {/*>>> FOOTER <<<*/}
        {!loading && !loadError && capabilities && (
          <div className="flex justify-end gap-2 border-t border-divider/50 bg-bg-3/60 px-5 py-4">
            <Button variant="ghost" size="sm" onClick={() => onClose?.()} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" form="modify-order-form" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        )}
      </div>

      {/* R6.6 — market-conversion confirm, blocks submit until explicit confirm */}
      <ConfirmDialog
        open={marketConfirmOpen}
        title="Convert to a market-executing order?"
        message={marketConvertMessage}
        confirmLabel="Convert & submit"
        cancelLabel="Go back"
        danger
        onConfirm={handleMarketConfirm}
        onCancel={handleMarketConfirmCancel}
      />
    </>
  );
};

export default ModifyOrderModal;
